import Heading2 from "components/Heading/Heading2";
import LoginModal from "components/LoginModal/LoginModal";
import StayCard from "components/StayCard/StayCard";
import { motion } from "framer-motion";
import { useAppDispatch, useAppSelector, useScrollToTop } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { FaHome } from "react-icons/fa";
import { HiOutlineHome } from "react-icons/hi";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchLocationsFiltered, getAllValidateLocations } from "redux/features/locations/locationSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Pagination from "shared/Pagination/Pagination";
import { SkeletonLoader } from "utils/SkeletonLoader";
import FiltersPage from "./FiltersPage";

export interface LocationsGridFilterCardProps {
  className?: string;
  data?: Location[];
  title?: string;
}

const LocationsGridFilterCard: FC<LocationsGridFilterCardProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const locationState = useAppSelector((state) => state.featureLocation?.locations);

  const [filters, setFilters] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const [page, setPage] = useState(initialPage);

  // Get all filter parameters from URL
  const event = searchParams.get("event");
  const region = searchParams.get("region");
  const category = searchParams.get("category");
  const visitor = searchParams.get("visitor");
  const minPrice = searchParams.get("minPrice");
  const maxPrice = searchParams.get("maxPrice");
  const title = searchParams.get("title");

  const pathname = location.pathname;
  const decodedPathname = decodeURIComponent(pathname);
  const afterEvent = decodedPathname.split("/event/")[1];
  useScrollToTop();

  // Build filters object from URL parameters
  const buildFiltersFromURL = () => {
    const urlFilters: any = {};

    if (event) urlFilters.events = event;
    if (region) urlFilters.region = region;
    if (category) urlFilters.category = category;
    if (visitor) urlFilters.visitor = visitor;
    if (minPrice) urlFilters.minPrice = minPrice;
    if (maxPrice) urlFilters.maxPrice = maxPrice;
    if (title) urlFilters.title = title;

    return urlFilters;
  };

  useEffect(() => {
    setIsLoading(true);
    const activePage = parseInt(searchParams.get("page") || "1", 10);
    setPage(activePage);

    const urlFilters = buildFiltersFromURL();
    setFilters(urlFilters);

    if (Object.keys(urlFilters).length > 0 || afterEvent) {
      if (afterEvent) {
        urlFilters.events = afterEvent;
      }
      dispatch(fetchLocationsFiltered({ pageNumber: activePage, filters: urlFilters })).then(() =>
        setIsLoading(false)
      );
    } else {
      dispatch(getAllValidateLocations(activePage)).then(() => setIsLoading(false));
    }
  }, [location.search, dispatch, afterEvent]);

  const handleApplyFilters = (newFilters: any) => {
    setIsLoading(true);
    const updatedFilters = { ...filters, ...newFilters };

    // Preserve event and region from URL if they exist
    if (event) updatedFilters.events = event;
    if (region) updatedFilters.region = region;

    setFilters(updatedFilters);

    dispatch(fetchLocationsFiltered({ pageNumber: page, filters: updatedFilters })).then(() =>
      setIsLoading(false)
    );
  };

  const handleApplyFiltersRegionEvent = (newFilters: any) => {
    setIsLoading(true);
    const filter = {
      ...filters, // Preserve existing filters
      region: newFilters.region,
      events: newFilters.event?.replace(/\s+/g, "-")?.toLowerCase(),
    };

    // Update the URL with all current filters
    const params = new URLSearchParams(location.search);

    // Handle region and event
    if (filter.region) {
      params.set("region", filter.region);
    } else {
      params.delete("region");
    }
    if (filter.events) {
      params.set("event", filter.events);
    } else {
      params.delete("event");
    }

    // Preserve other filter parameters
    if (category) params.set("category", category);
    if (visitor) params.set("visitor", visitor);
    if (minPrice) params.set("minPrice", minPrice);
    if (maxPrice) params.set("maxPrice", maxPrice);
    if (title) params.set("title", title);

    params.set("page", page.toString());
    navigate(`${location.pathname}?${params.toString()}`);

    dispatch(fetchLocationsFiltered({ pageNumber: page, filters: filter })).then(() => setIsLoading(false));
  };

  const handlePageChange = (newPage: any) => {
    setPage(newPage);
    const params = new URLSearchParams(location.search);
    params.set("page", newPage.toString());
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const [showLoginModal, setShowLoginModal] = useState(false);

  return (
    <div className={`nc-SectionGridFilterCard ${className}`} data-nc-id="SectionGridFilterCard">
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="flex flex-col items-center p-6 bg-white rounded-lg dark:bg-neutral-900">
              <div className="self-end mb-4">
                <ButtonSecondary onClick={() => setShowLoginModal(false)}>X</ButtonSecondary>
              </div>
              <LoginModal LoginModal={(e) => setShowLoginModal(e)} />
            </div>
          </div>
        </div>
      )}
      <div className="mt-5">
        <Heading2
          heading={
            <div className="flex items-center">
              <HiOutlineHome className="w-5 h-5 mr-2 text-gray-500 lg:w-5 lg:h-5" />
              <span>Espaces</span>
            </div>
          }
        />
      </div>

      <FiltersPage onApplyFilters={handleApplyFilters} onApplyFiltersRegionEvent={handleApplyFiltersRegionEvent} />
      <br />

      {isLoading ? (
        <div className="flex items-center justify-center mt-16">
          <SkeletonLoader />
        </div>
      ) : !locationState?.locations || locationState.count === 0 ? (
        <div className="flex flex-col items-center justify-center" style={{ height: "300px" }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="flex flex-col items-center text-center"
          >
            <FaHome className="mb-4 text-4xl text-gray-400" />
            <p className="text-lg font-semibold text-gray-600">Aucun espace disponible</p>
          </motion.div>
        </div>
      ) : (
        <motion.div
          className="grid grid-cols-1 gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {Array.isArray(locationState?.locations) &&
            locationState?.locations.map((stay: any, index: any) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
              >
                <StayCard
                  data={stay}
                  galleryImgs={stay.images}
                  showLoginModal={showLoginModal}
                  setShowLoginModal={setShowLoginModal}
                />
              </motion.div>
            ))}
        </motion.div>
      )}
      <div className="flex items-center justify-center mt-16">
        {locationState?.totalPages > 1 && (
          <Pagination currentPage={page} totalPages={locationState?.totalPages} onPageChange={handlePageChange} />
        )}
      </div>
    </div>
  );
};

export default LocationsGridFilterCard;
